/**
 * Leviathan Web — attach a lifting to the shipments it supplied
 * Quasar Prime LLC
 *
 * THE ORDERING PROBLEM this exists for.
 *
 * Liftings are usually keyed in AFTER the shipments they supplied: the load
 * goes out, the delivery is recorded the same day, and the terminal paperwork
 * arrives later — often in a batch. So recording origin on the shipment form
 * only ever reaches the operator who happened to hold the BOL at the right
 * moment. This is the other direction: enter the lifting, and be offered the
 * shipments it looks like it supplied.
 *
 * NOTHING IS ATTACHED AUTOMATICALLY, and that is deliberate.
 *
 * A match is a suggestion built from product, dates and quantities. None of
 * that is proof — a distributor running two loads of the same product in a week
 * will produce several equally plausible matches. Origin decides which state's
 * excise a load owes, so an assumed origin that LOOKS confirmed is worse than
 * no origin at all: the invoice would carry a wrong fact with no warning on it.
 * Every row starts unticked, and the evidence for each is shown so the operator
 * judges it rather than trusting a score.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Link2, Loader2, Lock, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { API_URL } from '@/config/api';
import { parseApiError } from '@/utils/errorHandler';

interface ShipmentMatch {
  liftingItemId: string;
  shipmentItemId: string;
  shipmentId: string;
  shipmentNumber: string;
  shipDate: string;
  shipmentStatus: string;
  customerName: string;
  productName: string;
  liftedGallons: string;
  shippedGallons: string;
  score: number;
  reasons: string[];
  hasOrigin: boolean;
  existingOrigin: string | null;
  locked: boolean;
  lockReason: string | null;
}

interface Props {
  liftingId: string;
  onAttached?: () => void;
}

export default function AttachToShipments({ liftingId, onAttached }: Props) {
  const [matches, setMatches] = useState<ShipmentMatch[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    attached: number;
    refused: Array<{ shipmentItemId: string; reason: string }>;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_URL}/api/terminals/liftings/${liftingId}/shipment-matches`
      );
      if (!response.ok) throw new Error(await parseApiError(response));
      const data = await response.json();
      setMatches(data.matches ?? []);
      // Selections are NOT restored across a reload — a refresh means the
      // underlying data may have moved, so the operator confirms again.
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load matching shipments.');
    } finally {
      setLoading(false);
    }
  }, [liftingId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (shipmentItemId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(shipmentItemId)) next.delete(shipmentItemId);
      else next.add(shipmentItemId);
      return next;
    });
  };

  const attach = async () => {
    const attachments = matches
      .filter((m) => selected.has(m.shipmentItemId) && !m.locked)
      .map((m) => ({ liftingItemId: m.liftingItemId, shipmentItemId: m.shipmentItemId }));

    if (attachments.length === 0) return;

    setSaving(true);
    setError(null);
    try {
      const response = await apiFetch(`${API_URL}/api/terminals/liftings/${liftingId}/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attachments }),
      });
      if (!response.ok) throw new Error(await parseApiError(response));
      const data = await response.json();
      setResult({ attached: data.attached?.length ?? 0, refused: data.refused ?? [] });
      await load();
      onAttached?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to attach.');
    } finally {
      setSaving(false);
    }
  };

  const selectable = matches.filter((m) => !m.locked);
  const selectedCount = matches.filter((m) => selected.has(m.shipmentItemId) && !m.locked).length;

  return (
    <div className="bg-panel-dark border border-panel-border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Link2 className="w-5 h-5 text-amber" />
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
            Attach to Shipments
          </h3>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading || saving}
          className="flex items-center space-x-2 px-3 py-1.5 border border-panel-border text-text-secondary hover:text-amber hover:border-amber text-xs font-mono uppercase transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      <p className="text-text-tertiary text-xs font-mono mb-4">
        &gt; These shipments carry the same product within the delivery window of this
        lifting. They are <span className="text-amber">suggestions, not matches</span> —
        origin decides which state&apos;s tax a load owes, so confirm each one against the
        BOL before attaching. Nothing is written until you press Attach.
      </p>

      {error && (
        <div className="flex items-start space-x-2 p-3 mb-4 bg-error-red/10 border border-error-red/50">
          <AlertTriangle className="w-4 h-4 text-error-red mt-0.5 flex-shrink-0" />
          <p className="text-error-red text-xs font-mono">{error}</p>
        </div>
      )}

      {result && (
        <div className="p-3 mb-4 bg-steel/10 border border-steel/40 space-y-2">
          <p className="text-text-secondary text-xs font-mono">
            <Check className="w-3 h-3 inline mr-1 text-green-400" />
            {result.attached} shipment line{result.attached === 1 ? '' : 's'} attached.
          </p>
          {result.refused.map((r) => (
            <p key={r.shipmentItemId} className="text-amber text-xs font-mono">
              &gt; Refused: {r.reason}
            </p>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center space-x-3 py-8 justify-center">
          <Loader2 className="w-4 h-4 text-amber animate-spin" />
          <span className="text-text-tertiary font-mono text-sm">Looking for shipments…</span>
        </div>
      ) : matches.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-panel-border">
          <p className="text-text-tertiary font-mono text-sm">
            No shipments of this product were delivered near this lifting date.
          </p>
          <p className="text-text-tertiary font-mono text-xs mt-2">
            Origin can still be set from the shipment itself.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {matches.map((match) => {
            const isSelected = selected.has(match.shipmentItemId);
            return (
              <div
                key={match.shipmentItemId}
                className={`p-4 border transition-colors ${
                  match.locked
                    ? 'border-panel-border bg-background/40 opacity-70'
                    : isSelected
                      ? 'border-amber bg-amber/5'
                      : 'border-panel-border hover:border-steel'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={match.locked || saving}
                    onChange={() => toggle(match.shipmentItemId)}
                    className="mt-1 accent-amber disabled:opacity-40"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                      <span className="text-amber font-mono text-sm">{match.shipmentNumber}</span>
                      <span className="text-text-primary text-sm">{match.customerName}</span>
                      <span className="text-text-tertiary font-mono text-xs">
                        {new Date(match.shipDate).toLocaleDateString()}
                      </span>
                      <span className="text-text-tertiary font-mono text-xs">
                        {match.shipmentStatus}
                      </span>
                      <span className="text-text-secondary font-mono text-xs">
                        {Number(match.shippedGallons).toLocaleString()} of{' '}
                        {Number(match.liftedGallons).toLocaleString()} gal lifted
                      </span>
                    </div>

                    <ul className="mt-2 space-y-0.5">
                      {match.reasons.map((reason) => (
                        <li key={reason} className="text-text-tertiary text-xs font-mono">
                          &middot; {reason}
                        </li>
                      ))}
                    </ul>

                    {match.locked && match.lockReason && (
                      <div className="flex items-start space-x-2 mt-2">
                        <Lock className="w-3 h-3 text-text-tertiary mt-0.5 flex-shrink-0" />
                        <p className="text-text-tertiary text-xs font-mono">{match.lockReason}</p>
                      </div>
                    )}
                    {!match.locked && match.hasOrigin && (
                      <div className="flex items-start space-x-2 mt-2">
                        <AlertTriangle className="w-3 h-3 text-amber mt-0.5 flex-shrink-0" />
                        <p className="text-amber text-xs font-mono">
                          Already recorded as {match.existingOrigin}. Attaching replaces it.
                        </p>
                      </div>
                    )}
                  </div>

                  {/*
                    The score ranks the list. It is shown small and last on
                    purpose: the reasons above it are what should be read, and a
                    prominent number invites ticking whatever scores highest.
                  */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-text-tertiary text-[10px] uppercase font-mono">Match</p>
                    <p className="text-text-secondary font-mono text-sm">{match.score}</p>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-between pt-3 border-t border-panel-border">
            <p className="text-text-tertiary text-xs font-mono">
              {selectedCount} of {selectable.length} selectable line
              {selectable.length === 1 ? '' : 's'} selected
            </p>
            <button
              type="button"
              onClick={attach}
              disabled={selectedCount === 0 || saving}
              className="px-6 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase text-sm tracking-wider transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Attaching…' : `Attach ${selectedCount || ''}`.trim()}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
